import type React from 'react';
import { coco } from '@cotera/api';
import type { ToolInputField } from '../tool-input-schema';
import type { ToolInputRefIssue } from '../tool-input-ref-issues';
import type { UpstreamSourceGroup } from '../variable-picker-data';
import { DatasetColumnsControl } from './dataset-columns.control';
import { DatasetConditionsControl } from './dataset-conditions.control';
import { DatasetSelectControl } from './dataset-select.control';
import { DatasetWriteControl } from './dataset-write.control';
import { WorkflowMultiSelectControl } from './workflow-multi-select.control';
import { BigQueryAppendControl } from './bigquery-append.control';
import { ColumnValueListControl } from './column-value-list.control';

/** Tool keys for the BigQuery tools (see `BigQuerySql*Tool.key`). */
const BIGQUERY_APPEND_TOOL_KEY = 'bigquery-sql/append';
const BIGQUERY_UPDATE_TOOL_KEY = 'bigquery-sql/update';

/**
 * What a custom control is handed in place of the generic one.
 *
 * It gets the whole `params` record, not just its own value, because a control
 * can legitimately depend on a sibling — the dataset write builder cannot offer
 * column names until it knows which dataset `datasetId` names.
 */
export type ToolInputControlProps = {
  readonly field: ToolInputField;
  readonly params: Readonly<Record<string, unknown>>;
  /**
   * The whole `paramBindings` record (field name -> upstream ref), not just this
   * field's — mirroring `params`, and for the same reason: a control reads its
   * own field's binding to notice when the value is supplied *whole* by an
   * upstream step rather than authored here.
   *
   * The dataset write control is the case that needs it. Its column mapping
   * lives in `params.rows`; when `rows` is instead bound whole to one upstream
   * ref (an agent purpose-built to emit the write shape), `params.rows` is empty
   * and the control has nothing to draw — so without this it renders a blank
   * node. With it, it draws a read-only notice naming the step the rows come
   * from instead.
   */
  readonly paramBindings: Readonly<Record<string, string>>;
  readonly sources: readonly UpstreamSourceGroup[];
  /**
   * The refs saved under this field that no longer read anything, already
   * narrowed to this field.
   *
   * Handed to the control rather than rendered above it, because only the
   * control knows which of the boxes it drew a given ref sits in — a dataset
   * write's issues are per column, and one message at the top of the section
   * would not say which column to fix.
   */
  readonly refIssues: readonly ToolInputRefIssue[];
  /**
   * Writes several params at once, clearing any stale upstream binding on each.
   *
   * A control that owns more than one field needs this rather than repeated
   * single-field calls: each of those rebuilds the param record from the render
   * that produced it, so two in a row would leave only the second.
   *
   * `undefined` removes a param.
   */
  readonly setValues: (patch: Readonly<Record<string, unknown>>) => void;
};

export type ToolInputControl = React.FC<ToolInputControlProps>;

type ToolInputOverride = {
  /** Field name to the control that replaces the generic editor for it. */
  readonly controls: Readonly<Record<string, ToolInputControl>>;
  /**
   * What a field is called in the editor, when its own name is a poor label.
   *
   * A tool input's name is an argument name, and the two coincide often enough
   * that the field name is the right default. `datasetId` is where they part:
   * the control below it picks a dataset by name and never shows an id, so
   * labelling it after the wire format describes the schema rather than the
   * thing being chosen. Only the label moves — the param keeps its name.
   */
  readonly labels?: Readonly<Record<string, string>>;
  /**
   * Fields another control on the same tool already owns.
   *
   * Without this they render a second time as generic inputs — a `keyColumns`
   * the dataset write control is maintaining would also appear as a JSON box
   * reading `null`, which is both confusing and editable into a contradiction.
   */
  readonly hidden?: readonly string[];
  /**
   * Fields whose value only means something for a particular value of another
   * field, keyed by the field they hang off.
   *
   * A dataset write's `rows` name columns of *that* dataset. Point the node at a
   * different one and those names are a claim about a schema that is no longer
   * there — the column picker repopulates, but the saved rows do not, and the
   * node goes on serializing and sending them until it fails at run time on
   * columns nobody can see in the editor any more. Changing the field they
   * depend on clears them.
   */
  readonly invalidates?: Readonly<Record<string, readonly string[]>>;
  /**
   * Optional fields to render up front rather than behind "Add input".
   *
   * The generic editor only shows a required field or one that already has a
   * value; everything else waits behind the picker. That is wrong for a node
   * whose primary inputs are all optional at the schema level — the Dataset
   * Query node can take a `symbol` instead of a `datasetId`, so neither can be
   * required, yet the dataset picker and its condition builder are the whole
   * point and must be visible on an empty node. Listing them here shows them by
   * default while leaving the advanced fields (symbol, raw SQL) in the picker.
   */
  readonly alwaysShow?: readonly string[];
};

/**
 * Per-tool overrides for how a tool's inputs are edited.
 *
 * The generic editor renders off the Erod type alone, which is right for almost
 * every tool: a string is a text box. It is wrong when the string is a uuid the
 * user is expected to know by heart, or when an array-of-structs is really a
 * two-mode form. Erod has no annotation channel that survives the trip to the
 * client — only field name, description and type do — so the knowledge lives
 * here instead of in the schema.
 */
const TOOL_INPUT_OVERRIDES: Readonly<Record<string, ToolInputOverride>> = {
  // The append tool takes `columns` + positional `rows`; one control owns both
  // so cells line up with columns instead of being hand-aligned in raw JSON.
  // Registered on `rows` (where the cell refs live, so their issues reach it),
  // with `columns` hidden because the control draws the column names itself.
  [BIGQUERY_APPEND_TOOL_KEY]: {
    controls: { rows: BigQueryAppendControl },
    labels: { rows: 'Rows to insert' },
    hidden: ['columns'],
  },
  // Update takes `set` (columns to write) and `where` (equality match). Both are
  // `{column, value}[]`; the same list control edits each, registered on its own
  // field so its cells' ref issues (keyed `<field>.<i>.value`) reach it.
  [BIGQUERY_UPDATE_TOOL_KEY]: {
    controls: {
      set: ColumnValueListControl,
      where: ColumnValueListControl,
    },
    labels: { set: 'Set columns', where: 'Match rows where' },
  },
  // The Dataset Query node targets a dataset resource by id and filters it with
  // a column-aware condition builder. The dataset picker, conditions, output
  // columns and limit lead; the advanced fields (raw SQL `query`, the ERA
  // `symbol` + its structured filters) wait behind "Add input".
  'dataset/query/execute': {
    controls: {
      datasetId: DatasetSelectControl,
      conditions: DatasetConditionsControl,
      columns: DatasetColumnsControl,
    },
    labels: { datasetId: 'dataset', conditions: 'filters' },
    alwaysShow: ['datasetId', 'conditions', 'columns', 'limit'],
    // Point the node at another dataset and the filters/columns name columns
    // that may not exist there; clear them so the builder repopulates clean.
    invalidates: { datasetId: ['conditions', 'columns'] },
  },
  [coco.COCO_DATASET_READ_TOOL_KEY]: {
    controls: {
      datasetId: DatasetSelectControl,
      columns: DatasetColumnsControl,
    },
    labels: { datasetId: 'dataset' },
    // `limit` and `offset` survive: they are about the page, not the dataset.
    invalidates: { datasetId: ['columns'] },
  },
  [coco.COCO_DATASET_EDIT_TOOL_KEY]: {
    controls: { datasetId: DatasetSelectControl },
    labels: { datasetId: 'dataset' },
    invalidates: { datasetId: ['edits'] },
  },
  [coco.COCO_DATASET_WRITE_TOOL_KEY]: {
    controls: { datasetId: DatasetSelectControl, rows: DatasetWriteControl },
    labels: { datasetId: 'dataset' },
    hidden: ['keyColumns'],
    invalidates: { datasetId: ['rows', 'keyColumns'] },
  },
  [coco.COCO_RUN_DATASET_WORKFLOWS_TOOL_KEY]: {
    controls: {
      datasetId: DatasetSelectControl,
      workflowIds: WorkflowMultiSelectControl,
    },
    labels: { datasetId: 'dataset', workflowIds: 'workflows' },
    // The workflow picker offers the workflows bound to the *current* dataset;
    // once that changes, a saved id can name a workflow bound to the old one
    // and nothing on the new. Clear it so the selection always describes the
    // dataset the node now points at.
    invalidates: { datasetId: ['workflowIds'] },
  },
};

/** What this field is called in the editor — its own name unless overridden. */
export const toolInputFieldLabel = (
  toolKey: string | undefined,
  fieldName: string
): string =>
  (toolKey === undefined
    ? undefined
    : TOOL_INPUT_OVERRIDES[toolKey]?.labels?.[fieldName]) ?? fieldName;

/** The custom control for this tool's field, or null to render the generic one. */
export const resolveToolInputControl = (
  toolKey: string | undefined,
  fieldName: string
): ToolInputControl | null => {
  if (toolKey === undefined) {
    return null;
  }
  return TOOL_INPUT_OVERRIDES[toolKey]?.controls[fieldName] ?? null;
};

/**
 * Fields whose saved value stops meaning anything once `fieldName` changes.
 *
 * See {@link ToolInputOverride.invalidates}. Empty for a field nothing hangs
 * off, which is almost all of them.
 */
export const toolInputFieldsInvalidatedBy = (
  toolKey: string | undefined,
  fieldName: string
): readonly string[] =>
  toolKey === undefined
    ? []
    : TOOL_INPUT_OVERRIDES[toolKey]?.invalidates?.[fieldName] ?? [];

/**
 * The params a control's write has just made meaningless, set to be removed.
 *
 * Applied to every custom control's `setValues`, so a control never has to know
 * what hangs off the field it owns. Nothing is cleared when the value did not
 * actually change, which is what keeps re-picking the same dataset from
 * throwing away the rows already configured for it.
 */
export const staleParamsAfterChange = (args: {
  readonly field: string;
  readonly patch: Readonly<Record<string, unknown>>;
  readonly params: Readonly<Record<string, unknown>>;
  readonly toolKey?: string;
}): Readonly<Record<string, unknown>> => {
  if (
    !(args.field in args.patch) ||
    args.patch[args.field] === args.params[args.field]
  ) {
    return {};
  }
  return Object.fromEntries(
    toolInputFieldsInvalidatedBy(args.toolKey, args.field).map((name) => [
      name,
      undefined,
    ])
  );
};

/** Fields this tool's own controls maintain, which must not render themselves. */
export const hiddenToolInputFields = (
  toolKey: string | undefined
): ReadonlySet<string> =>
  new Set(
    toolKey === undefined ? [] : TOOL_INPUT_OVERRIDES[toolKey]?.hidden ?? []
  );

/** Optional fields this tool renders up front rather than behind "Add input". */
export const alwaysShownToolInputFields = (
  toolKey: string | undefined
): ReadonlySet<string> =>
  new Set(
    toolKey === undefined ? [] : TOOL_INPUT_OVERRIDES[toolKey]?.alwaysShow ?? []
  );
